import { useState, useRef } from 'react';
import { CalendarEvent, Propuesta, Gestion } from '../types';

/**
 * Modal state management types
 */
export interface ModalManagerState {
  // Modal open states
  isReunionModalOpen: boolean;
  isPropuestaModalOpen: boolean;
  isGestionModalOpen: boolean;
  isDayViewModalOpen: boolean;

  // Modal data
  modalDefaultDate: string | null;
  selectedDayEvents: CalendarEvent[];
  selectedDayDate: string | null;
  selectedReunionData: CalendarEvent | null;
  selectedReunionForAssignment: CalendarEvent | null;
  selectedPropuestaData: Propuesta | null;
  selectedGestionData: Gestion | null;
  sourceReunionForBack: CalendarEvent | null;

  // Refresh triggers
  propuestasRefreshTrigger: number;
  gestionesRefreshTrigger: number;
  reunionesRefreshTrigger: number;

  // Reunion modal refresh ref
  reunionModalRefreshRef: React.MutableRefObject<(() => Promise<void>) | null>;
}

/**
 * Modal manager actions
 */
export interface ModalManagerActions {
  // Open modals with context
  openReunionModal: (date?: string, data?: CalendarEvent) => void;
  openPropuestaModal: (date?: string, reunion?: CalendarEvent, data?: Propuesta) => void;
  openGestionModal: (date?: string, reunion?: CalendarEvent, data?: Gestion) => void;
  openDayViewModal: (date: string, events: CalendarEvent[]) => void;

  // Close modals
  closeReunionModal: () => void;
  closePropuestaModal: () => void;
  closeGestionModal: () => void;
  closeDayViewModal: () => void;

  // Open details
  openReunionDetails: (event: CalendarEvent) => void;
  openPropuestaDetails: (data: Propuesta, sourceReunion?: CalendarEvent) => void;
  openGestionDetails: (data: Gestion, sourceReunion?: CalendarEvent) => void;

  // Navigation
  backToReunion: () => void;

  // Refresh triggers
  triggerPropuestasRefresh: () => void;
  triggerGestionesRefresh: () => void;
  triggerReunionesRefresh: () => void;

  // Update state
  setSelectedDayEvents: (events: CalendarEvent[]) => void;
  setReunionModalRefresh: (fn: () => Promise<void>) => void;
}

/**
 * useModalManager - Centralized modal state management
 *
 * Manages all modal states, data, and interactions for the application.
 * Handles opening/closing modals, tracking selected entities, and managing
 * navigation between related modals (e.g., from reunion to propuesta/gestion).
 */
export function useModalManager(): ModalManagerState & ModalManagerActions {
  // Modal open states
  const [isReunionModalOpen, setIsReunionModalOpen] = useState(false);
  const [isPropuestaModalOpen, setIsPropuestaModalOpen] = useState(false);
  const [isGestionModalOpen, setIsGestionModalOpen] = useState(false);
  const [isDayViewModalOpen, setIsDayViewModalOpen] = useState(false);

  // Modal data states
  const [modalDefaultDate, setModalDefaultDate] = useState<string | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarEvent[]>([]);
  const [selectedDayDate, setSelectedDayDate] = useState<string | null>(null);
  const [selectedReunionData, setSelectedReunionData] = useState<CalendarEvent | null>(null);
  const [selectedReunionForAssignment, setSelectedReunionForAssignment] = useState<CalendarEvent | null>(null);
  const [selectedPropuestaData, setSelectedPropuestaData] = useState<Propuesta | null>(null);
  const [selectedGestionData, setSelectedGestionData] = useState<Gestion | null>(null);
  const [sourceReunionForBack, setSourceReunionForBack] = useState<CalendarEvent | null>(null);

  // Refresh triggers
  const [propuestasRefreshTrigger, setPropuestasRefreshTrigger] = useState(0);
  const [gestionesRefreshTrigger, setGestionesRefreshTrigger] = useState(0);
  const [reunionesRefreshTrigger, setReunionesRefreshTrigger] = useState(0);

  // Reunion modal refresh ref
  const reunionModalRefreshRef = useRef<(() => Promise<void>) | null>(null);

  // Open modals with context
  const openReunionModal = (date?: string, data?: CalendarEvent) => {
    setModalDefaultDate(date || new Date().toISOString().split('T')[0]);
    setSelectedReunionData(data || null);
    setSelectedReunionForAssignment(null);
    setIsReunionModalOpen(true);
  };

  const openPropuestaModal = (date?: string, reunion?: CalendarEvent, data?: Propuesta) => {
    setModalDefaultDate(date || new Date().toISOString().split('T')[0]);
    setSelectedReunionForAssignment(reunion || null);
    setSelectedPropuestaData(data || null);
    setIsPropuestaModalOpen(true);
  };

  const openGestionModal = (date?: string, reunion?: CalendarEvent, data?: Gestion) => {
    setModalDefaultDate(date || new Date().toISOString().split('T')[0]);
    setSelectedReunionForAssignment(reunion || null);
    setSelectedGestionData(data || null);
    setIsGestionModalOpen(true);
  };

  const openDayViewModal = (date: string, events: CalendarEvent[]) => {
    setSelectedDayDate(date);
    setSelectedDayEvents(events);
    setIsDayViewModalOpen(true);
  };

  // Close modals
  const closeReunionModal = () => setIsReunionModalOpen(false);
  const closePropuestaModal = () => setIsPropuestaModalOpen(false);
  const closeGestionModal = () => setIsGestionModalOpen(false);
  const closeDayViewModal = () => setIsDayViewModalOpen(false);

  // Open details
  const openReunionDetails = (event: CalendarEvent) => {
    const data: CalendarEvent = {
      ...event,
      municipality: event.municipality || 'Chihuahua',
      organizer: event.organizer || 'Juan Pérez',
      phone: event.phone || event.organizer_phone || '(614) 123-4567',
      notes: event.notes || event.description || 'Notas de la reunión...'
    };
    setSelectedReunionData(data);
    setIsReunionModalOpen(true);
  };

  const openPropuestaDetails = (data: Propuesta, sourceReunion?: CalendarEvent) => {
    setSelectedPropuestaData(data);
    if (sourceReunion) {
      setSourceReunionForBack(sourceReunion);
    }
    setIsPropuestaModalOpen(true);
  };

  const openGestionDetails = (data: Gestion, sourceReunion?: CalendarEvent) => {
    setSelectedGestionData(data);
    if (sourceReunion) {
      setSourceReunionForBack(sourceReunion);
    }
    setIsGestionModalOpen(true);
  };

  // Navigation
  const backToReunion = () => {
    setIsPropuestaModalOpen(false);
    setIsGestionModalOpen(false);
    setSelectedPropuestaData(null);
    setSelectedGestionData(null);

    if (sourceReunionForBack) {
      setSelectedReunionData(sourceReunionForBack);
      setIsReunionModalOpen(true);
    }

    setSourceReunionForBack(null);
  };

  // Refresh triggers
  const triggerPropuestasRefresh = () => setPropuestasRefreshTrigger(prev => prev + 1);
  const triggerGestionesRefresh = () => setGestionesRefreshTrigger(prev => prev + 1);
  const triggerReunionesRefresh = () => setReunionesRefreshTrigger(prev => prev + 1);

  // Update state
  const setReunionModalRefresh = (fn: () => Promise<void>) => {
    reunionModalRefreshRef.current = fn;
  };

  return {
    // State
    isReunionModalOpen,
    isPropuestaModalOpen,
    isGestionModalOpen,
    isDayViewModalOpen,
    modalDefaultDate,
    selectedDayEvents,
    selectedDayDate,
    selectedReunionData,
    selectedReunionForAssignment,
    selectedPropuestaData,
    selectedGestionData,
    sourceReunionForBack,
    propuestasRefreshTrigger,
    gestionesRefreshTrigger,
    reunionesRefreshTrigger,
    reunionModalRefreshRef,

    // Actions
    openReunionModal,
    openPropuestaModal,
    openGestionModal,
    openDayViewModal,
    closeReunionModal,
    closePropuestaModal,
    closeGestionModal,
    closeDayViewModal,
    openReunionDetails,
    openPropuestaDetails,
    openGestionDetails,
    backToReunion,
    triggerPropuestasRefresh,
    triggerGestionesRefresh,
    triggerReunionesRefresh,
    setSelectedDayEvents,
    setReunionModalRefresh,
  };
}
